import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { Pencil, Trash2, Plus, ExternalLink, Copy, Monitor } from "lucide-react";

const empty = { name: "", scope: "all", room_ids: [], floor: "", view_mode: "now_next" };

export default function AdminDisplays() {
  const [displays, setDisplays] = useState([]);
  const [rooms, setRooms] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);
  const [editId, setEditId] = useState(null);

  const load = async () => {
    try {
      const [d, r] = await Promise.all([api.get("/displays"), api.get("/rooms")]);
      setDisplays(d.data);
      setRooms(r.data);
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  useEffect(() => {
    load();
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    try {
      if (editId) await api.put(`/displays/${editId}`, form);
      else await api.post("/displays", form);
      toast.success("Gemt");
      setOpen(false);
      setForm(empty);
      setEditId(null);
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Slet display?")) return;
    try {
      await api.delete(`/displays/${id}`);
      toast.success("Slettet");
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const toggleRoom = (id) => {
    setForm((f) => ({
      ...f,
      room_ids: f.room_ids.includes(id) ? f.room_ids.filter((x) => x !== id) : [...f.room_ids, id],
    }));
  };

  const copyUrl = (code) => {
    const url = `${window.location.origin}/display/${code}`;
    navigator.clipboard.writeText(url);
    toast.success("URL kopieret");
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-display text-3xl md:text-4xl font-bold tracking-tight">Display skærme</h1>
          <p className="text-slate-500 mt-1">Konfigurer iPad display-skærme i fællesarealer</p>
        </div>
        <Button
          onClick={() => {
            setForm(empty);
            setEditId(null);
            setOpen(true);
          }}
          className="rounded-xl bg-slate-900 hover:bg-slate-800 gap-2 h-11"
          data-testid="new-display-button"
        >
          <Plus className="h-4 w-4" /> Ny display-skærm
        </Button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {displays.map((d) => (
          <Card key={d.id} className="p-6 rounded-2xl border-slate-200" data-testid={`display-card-${d.id}`}>
            <div className="flex items-start justify-between">
              <div>
                <div className="flex items-center gap-2 mb-1">
                  <Monitor className="h-4 w-4 text-slate-500" />
                  <h3 className="font-display font-semibold text-lg">{d.name}</h3>
                </div>
                <p className="text-xs text-slate-500">
                  Visning:{" "}
                  {d.scope === "all"
                    ? "Alle lokaler"
                    : d.scope === "floor"
                      ? `Etage: ${d.floor || "—"}`
                      : `Udvalgte lokaler`}{" "}
                  · Tilstand:{" "}
                  {d.view_mode === "now_next" ? "Nu & næste" : d.view_mode === "day" ? "Dag" : "Uge"}
                </p>
              </div>
              <code className="text-xs font-mono bg-slate-100 px-2 py-1 rounded">{d.code}</code>
            </div>
            <div className="flex flex-wrap gap-2 mt-4">
              <Button variant="outline" size="sm" className="rounded-xl" onClick={() => copyUrl(d.code)} data-testid={`copy-${d.id}`}>
                <Copy className="h-3 w-3 mr-1" /> Kopiér URL
              </Button>
              <Button variant="outline" size="sm" className="rounded-xl" asChild>
                <a href={`/display/${d.code}`} target="_blank" rel="noreferrer" data-testid={`open-${d.id}`}>
                  <ExternalLink className="h-3 w-3 mr-1" /> Åbn
                </a>
              </Button>
              <Button
                variant="outline"
                size="sm"
                className="rounded-xl"
                onClick={() => {
                  setForm({
                    name: d.name,
                    scope: d.scope,
                    room_ids: d.room_ids || [],
                    floor: d.floor || "",
                    view_mode: d.view_mode,
                  });
                  setEditId(d.id);
                  setOpen(true);
                }}
                data-testid={`edit-display-${d.id}`}
              >
                <Pencil className="h-3 w-3 mr-1" /> Rediger
              </Button>
              <Button variant="outline" size="sm" className="rounded-xl text-red-600" onClick={() => remove(d.id)} data-testid={`delete-display-${d.id}`}>
                <Trash2 className="h-3 w-3" />
              </Button>
            </div>
          </Card>
        ))}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="rounded-2xl sm:max-w-[520px] max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl">
              {editId ? "Rediger display" : "Ny display-skærm"}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={submit} className="space-y-4">
            <div className="space-y-2">
              <Label>Navn</Label>
              <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required className="h-11 rounded-xl" data-testid="display-name-input" />
            </div>
            <div className="space-y-2">
              <Label>Omfang</Label>
              <Select value={form.scope} onValueChange={(v) => setForm({ ...form, scope: v })}>
                <SelectTrigger className="h-11 rounded-xl">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Alle lokaler</SelectItem>
                  <SelectItem value="floor">Én etage</SelectItem>
                  <SelectItem value="single">Udvalgte lokaler</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {form.scope === "floor" && (
              <div className="space-y-2">
                <Label>Etage navn</Label>
                <Input value={form.floor} onChange={(e) => setForm({ ...form, floor: e.target.value })} className="h-11 rounded-xl" placeholder="Fx 1. sal" />
              </div>
            )}
            {form.scope === "single" && (
              <div className="space-y-2">
                <Label>Vælg lokaler</Label>
                <div className="space-y-2 max-h-40 overflow-y-auto p-3 bg-slate-50 rounded-xl">
                  {rooms.map((r) => (
                    <label key={r.id} className="flex items-center gap-2 text-sm">
                      <Checkbox checked={form.room_ids.includes(r.id)} onCheckedChange={() => toggleRoom(r.id)} />
                      <span className="w-2.5 h-2.5 rounded-full" style={{ background: r.color }} />
                      {r.name}
                    </label>
                  ))}
                </div>
              </div>
            )}
            <div className="space-y-2">
              <Label>Visningstilstand</Label>
              <Select value={form.view_mode} onValueChange={(v) => setForm({ ...form, view_mode: v })}>
                <SelectTrigger className="h-11 rounded-xl">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="now_next">Nu og næste booking</SelectItem>
                  <SelectItem value="day">Dagsoversigt</SelectItem>
                  <SelectItem value="week">Ugeoversigt</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setOpen(false)} className="rounded-xl">
                Annullér
              </Button>
              <Button type="submit" className="rounded-xl bg-slate-900 hover:bg-slate-800" data-testid="save-display-button">
                Gem
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
